import type { SxProps, Theme } from '@mui/material'

import type { BubbleStyleConfig } from '../defaults'
import { DEFAULT_BUBBLE_STYLES } from '../defaults'

export type BorderPosition = 'left' | 'right' | 'none'

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const sanitized = hex.replace('#', '')
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(sanitized)
  if (!result || result.length < 4) {
    return null
  }
  const rHex = result[1]
  const gHex = result[2]
  const bHex = result[3]
  if (!rHex || !gHex || !bHex) {
    return null
  }
  return {
    r: Number.parseInt(rHex, 16),
    g: Number.parseInt(gHex, 16),
    b: Number.parseInt(bHex, 16),
  }
}

function resolveThemeColor(theme: Theme, colorKey: string): string {
  if (colorKey.startsWith('rgba') || colorKey.startsWith('rgb') || colorKey.startsWith('#')) {
    return colorKey
  }

  const parts = colorKey.split('.')
  if (parts.length === 2) {
    const palette = parts[0]
    const shade = parts[1]
    if (palette && shade) {
      const paletteObj = theme.palette[palette as keyof typeof theme.palette]
      if (paletteObj && typeof paletteObj === 'object' && shade in paletteObj) {
        const colorValue = (paletteObj as unknown as Record<string, string>)[shade]
        if (colorValue) {
          return colorValue
        }
      }
    }
  }

  return colorKey
}

export function createBorderColorWithOpacity(
  theme: Theme,
  colorKey: string,
  opacity: number,
): string {
  const resolvedColor = resolveThemeColor(theme, colorKey)

  if (resolvedColor.startsWith('rgba')) {
    return resolvedColor.replace(/[\d.]+\)$/, `${opacity})`)
  }

  if (resolvedColor.startsWith('rgb(')) {
    const rgbMatch = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/.exec(resolvedColor)
    if (rgbMatch) {
      return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${opacity})`
    }
  }

  const rgb = hexToRgb(resolvedColor)
  if (rgb) {
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`
  }

  return resolvedColor
}

export function createBubbleContainerSx(config?: Partial<BubbleStyleConfig>): SxProps<Theme> {
  const merged = { ...DEFAULT_BUBBLE_STYLES.bubbles.agent, ...config }
  return {
    width: '100%',
    mb: merged.marginBottom,
  }
}

export function createBubblePaperSx(
  theme: Theme,
  borderColor: string,
  borderPosition: BorderPosition,
  config?: Partial<BubbleStyleConfig>,
): SxProps<Theme> {
  const merged = { ...DEFAULT_BUBBLE_STYLES.bubbles.agent, ...config }
  const borderColorWithOpacity = createBorderColorWithOpacity(
    theme,
    borderColor,
    merged.borderOpacity,
  )

  const borderStyles: Record<string, string | undefined> = {
    borderLeft:
      borderPosition === 'left'
        ? `${merged.borderWidth}px solid ${borderColorWithOpacity}`
        : undefined,
    borderRight:
      borderPosition === 'right'
        ? `${merged.borderWidth}px solid ${borderColorWithOpacity}`
        : undefined,
  }

  return {
    p: merged.padding,
    backgroundColor: merged.backgroundColor,
    borderRadius: merged.borderRadius,
    ...borderStyles,
    width: '100%',
    '&:hover .bubble-footer': {
      maxHeight: '40px',
      opacity: 1,
      mt: 1.5,
      pt: 0,
      transitionDelay: '1s',
    },
    '&:hover .copy-button': {
      opacity: 1,
      transitionDelay: '1s',
    },
    '&:hover .event-details-link': {
      opacity: 1,
      transitionDelay: '1s',
    },
    '@media (hover: none)': {
      '& .bubble-footer': {
        maxHeight: '40px',
        opacity: 1,
        mt: 1.5,
      },
      '& .event-details-link': {
        opacity: 1,
      },
    },
  }
}

export function createActorHeaderContainerSx(isAgent: boolean, isFirst: boolean): SxProps<Theme> {
  const { actorHeader } = DEFAULT_BUBBLE_STYLES
  return {
    display: 'flex',
    alignItems: 'center',
    flexDirection: isAgent ? 'row' : 'row-reverse',
    gap: 1.5,
    mb: actorHeader.marginBottom,
    mt: isFirst ? actorHeader.marginTop : 0,
    width: '100%',
  }
}

export function createActorLabelSx(theme: Theme): SxProps<Theme> {
  const { actorHeader } = DEFAULT_BUBBLE_STYLES
  const isDark = theme.palette.mode === 'dark'
  return {
    fontSize: actorHeader.fontSize,
    fontWeight: actorHeader.fontWeight,
    letterSpacing: actorHeader.letterSpacing,
    textTransform: actorHeader.textTransform,
    color: isDark ? actorHeader.color : 'rgba(62, 39, 35, 0.4)',
    flexShrink: 0,
  }
}

export function createActorLineSx(theme: Theme): SxProps<Theme> {
  const { actorHeader } = DEFAULT_BUBBLE_STYLES
  const isDark = theme.palette.mode === 'dark'
  const lineColor = isDark ? actorHeader.line.color : 'rgba(62, 39, 35, 0.08)'
  return {
    flex: 1,
    height: 0,
    borderTop: `${actorHeader.line.width}px ${actorHeader.line.style} ${lineColor}`,
  }
}

export const footerContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-start',
  mt: 0,
  pt: 0,
  maxHeight: 0,
  overflow: 'hidden',
  opacity: 0,
  transition:
    'max-height 0.2s ease-in-out, opacity 0.2s ease-in-out, margin-top 0.2s ease-in-out, padding-top 0.2s ease-in-out',
}

export const copyButtonContainerSx: SxProps<Theme> = {
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  marginLeft: 'auto',
}
